import type { ThemeColors } from "utils/themeContextHelper";
import {
  isCardFace,
  isDarkInk,
} from "components/common/cards/CompanyPaymentCard";
import type {
  CardArt,
  CardFace,
} from "components/common/cards/CompanyPaymentCard";

/**
 * The card rules both Card tabs share: which face and text colour a record
 * wears, and what Reset stages. Pure, so the company and platform pages read a
 * record the same way the card itself does.
 */

/** The older artwork rule, which still decides a card until a face is saved. */
export const legacyArtMode = (
  colors: Partial<ThemeColors>,
  hasOwnArt: boolean,
): CardArt =>
  (colors.paymentCardArt || (hasOwnArt ? "own" : "default")) as CardArt;

/**
 * The card as a company actually wears it: its own card keys over the
 * platform's, merged the way the server merges them, and which of the face and
 * the text colour it inherits rather than chose.
 */
export const effectiveCard = (
  own: Partial<ThemeColors>,
  platform: Partial<ThemeColors>,
  hasOwnArt: boolean,
) => {
  const pick = (key: keyof ThemeColors) => own[key] || platform[key] || "";
  const card: Partial<ThemeColors> = {
    paymentCardFace: pick("paymentCardFace"),
    paymentCardTextMode: pick("paymentCardTextMode"),
    paymentCardArt: pick("paymentCardArt"),
    paymentCardBackground: pick("paymentCardBackground"),
  };
  const face = card.paymentCardFace || "";
  const faceIsChosen = isCardFace(face);
  const artMode = legacyArtMode(card, hasOwnArt);
  // A saved face is what the radio shows. Before one is saved, the default
  // card IS the stock face, and a gradient or custom colour matches none.
  const shownFace: CardFace | "" = faceIsChosen
    ? face
    : artMode === "default"
      ? "stock"
      : "";
  return {
    card,
    face,
    faceIsChosen,
    artMode,
    shownFace,
    faceInherited: !own.paymentCardFace && isCardFace(platform.paymentCardFace),
    textInherited: !own.paymentCardTextMode && !!platform.paymentCardTextMode,
    isLightText: !isDarkInk(card.paymentCardTextMode),
  };
};

/**
 * What Reset stages for the card: back to the platform's face and text colour
 * where it sets them, and otherwise to the stock face, which outranks an older
 * artwork rule still stored.
 */
export const cardResetPatch = (
  platform: Partial<ThemeColors>,
  shownFace: CardFace | "",
  faceIsChosen: boolean,
): Partial<ThemeColors> => ({
  ...(isCardFace(platform.paymentCardFace)
    ? { paymentCardFace: "" }
    : shownFace === "stock" && !faceIsChosen
      ? {}
      : { paymentCardFace: "stock" }),
  paymentCardTextMode: "",
});
